// Library Imports
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Local Imports
import { ADMIN_MODULES, normalizeRole, ROLES } from '@/router/roles';

export const useRoutePermissions = () => {
  const currentUser = useSelector(state => state?.currentUser?.currentUser);

  const userRole = normalizeRole(currentUser?.userRole);
  const planType = currentUser?.planType || 'free';
  const isNewUser = currentUser?.isNewUser || false;

  const baseRoutes = useMemo(() => {
    if (userRole === ROLES.ADMIN) {
      return ADMIN_MODULES;
    }

    if (userRole === ROLES.BRAND) {
      return ['brands'];
    }

    if (userRole === ROLES.CREATOR) {
      return [
        'media-kit',
        'media-kit-desktop-preview',
        'shareable-link',
        'authenticate-social',
        'brands-reviews',
        'kit-management',
        'gifted',
        'paid',
        'profile-account',
        'tiktok-authentication',
      ];
    }

    return [];
  }, [userRole]);

  const allowedRoutes = useMemo(() => {
    if (userRole === ROLES.ADMIN || userRole === ROLES.BRAND) {
      return baseRoutes;
    }

    switch (planType) {
      case 'free':
        return [...baseRoutes, 'upgrade-media-kit'];
      case 'paid':
        return [...baseRoutes, 'manage-subscription'];
      case 'cancelled':
        return [...baseRoutes, 'manage-subscription'];
      default:
        return baseRoutes;
    }
  }, [planType, baseRoutes, userRole]);

  const canAccessRoute = (moduleName) => {
    const module = moduleName?.toLowerCase();
    if (!module) return false;

    if (userRole === ROLES.ADMIN) {
      return ADMIN_MODULES.includes(module);
    }

    if (userRole === ROLES.BRAND) {
      return module === 'brands';
    }

    if (userRole === ROLES.CREATOR) {
      return allowedRoutes.includes(module);
    }

    return false;
  };

  return {
    userRole,
    planType,
    isNewUser,
    baseRoutes,
    allowedRoutes,
    canAccessRoute,
  };
};
